import React, { useState, useRef } from 'react';
import './VideoCarousel.scss';

const VideoCarousel = ({ videos = [] }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const thumbnailRef = useRef(null);

  // Default videos if none provided
  const defaultVideos = [
    {
      id: 1,
      title: "Video 1",
      videoUrl: "https://www.youtube.com/watch?v=W9rCTfhQvVs",
      thumbnail: "http://i3.ytimg.com/vi/W9rCTfhQvVs/hqdefault.jpg"
    },
    {
      id: 2,
      title: "Video 2", 
      videoUrl: "https://www.youtube.com/watch?v=pyEozedPBrY",
      thumbnail: "http://i3.ytimg.com/vi/pyEozedPBrY/hqdefault.jpg"
    },
    {
      id: 3,
      title: "Video 3",
      videoUrl: "https://www.youtube.com/watch?v=j_tK2T1nuaA",
      thumbnail: "http://i3.ytimg.com/vi/j_tK2T1nuaA/hqdefault.jpg"
    }
  ];

  const videoList = videos.length > 0 ? videos : defaultVideos;
  const currentVideo = videoList[currentVideoIndex];

  // Ensure first video thumbnail is visible on component mount
  React.useEffect(() => {
    if (thumbnailRef.current && currentVideoIndex === 0) {
      scrollThumbnailIntoView(0);
    }
  }, []);

  // Function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Function to get YouTube thumbnail URL
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return `https://i3.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
    return null;
  };

  // Function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  // Function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    return null;
  };

  const handleVideoSelect = (index) => {
    setCurrentVideoIndex(index);
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    if (isYouTubeUrl(currentVideo?.videoUrl)) {
      // For YouTube videos, toggle playing state
      setIsPlaying(!isPlaying);
    } else {
      // For regular video files
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    }
  };

  const handlePrevious = () => {
    const newIndex = currentVideoIndex > 0 ? currentVideoIndex - 1 : videoList.length - 1;
    setCurrentVideoIndex(newIndex);
    setIsPlaying(false);
    
    // Scroll thumbnail into view
    scrollThumbnailIntoView(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentVideoIndex < videoList.length - 1 ? currentVideoIndex + 1 : 0;
    setCurrentVideoIndex(newIndex);
    setIsPlaying(false);
    
    // Scroll thumbnail into view
    scrollThumbnailIntoView(newIndex);
  };

  const scrollThumbnailIntoView = (index) => {
    if (thumbnailRef.current) {
      const thumbnail = thumbnailRef.current.children[index];
      if (thumbnail) {
        thumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  };


  return (
    <div className="video-carousel">
      {/* Main Video Area */}
      <div className="video-carousel__main">
        <div className="video-carousel__video-container">
          {/* Thumbnail Background for YouTube videos when not playing */}
          {isYouTubeUrl(currentVideo?.videoUrl) && !isPlaying && (
            <div 
              className="video-carousel__thumbnail-background"
              style={{
                backgroundImage: `url(${currentVideo?.thumbnail || getYouTubeThumbnail(currentVideo?.videoUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
          )}
          
          {isYouTubeUrl(currentVideo?.videoUrl) ? (
            <iframe
              ref={videoRef}
              className="video-carousel__video"
              src={isPlaying ? getYouTubeEmbedUrl(currentVideo?.videoUrl) : ''}
              title={currentVideo?.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              ref={videoRef}
              className="video-carousel__video"
              poster={currentVideo?.thumbnail || getYouTubeThumbnail(currentVideo?.videoUrl)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            >
              <source src={currentVideo?.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
          
          {/* Play Button Overlay */}
          {!isPlaying && (
            <button 
              className="video-carousel__play-button"
              onClick={handlePlayPause}
              aria-label="Play video"
            >
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="rgba(0,0,0,0.7)"/>
                <path d="M32 25L32 55L55 40L32 25Z" fill="white"/>
              </svg>
            </button>
          )}
        </div>

        {/* Navigation Arrows */}
        <button 
          className="video-carousel__nav video-carousel__nav--left"
          onClick={handlePrevious}
          aria-label="Previous video"
        >
          <img 
            src="/assets/images/image-slide-left.png" 
            alt="Previous" 
            className="video-carousel__nav-image"
          />
        </button>

        <button 
          className="video-carousel__nav video-carousel__nav--right"
          onClick={handleNext}
          aria-label="Next video"
        >
          <img 
            src="/assets/images/image-slide-right.png" 
            alt="Next" 
            className="video-carousel__nav-image"
          />
        </button>
      </div>

      {/* Thumbnail Carousel */}
      <div className="video-carousel__thumbnails">
        <div 
          className="video-carousel__thumbnails-container" 
          ref={thumbnailRef}
        >
          {videoList.map((video, index) => (
            <button
              key={video.id}
              className={`video-carousel__thumbnail ${index === currentVideoIndex ? 'video-carousel__thumbnail--active' : ''}`}
              onClick={() => handleVideoSelect(index)}
              aria-label={`Select video ${index + 1}`}
            >
              {video.thumbnail || getYouTubeThumbnail(video.videoUrl) ? (
                <>
                  <img 
                    src={video.thumbnail || getYouTubeThumbnail(video.videoUrl)} 
                    alt={video.title}
                    className="video-carousel__thumbnail-image"
                  />
                  <div className="video-carousel__thumbnail-overlay">
                    <div className="video-carousel__thumbnail-play-icon">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="16" fill="rgba(0, 0, 0, 0.7)"/>
                        <path d="M12 10L12 22L22 16L12 10Z" fill="white"/>
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <div className="video-carousel__thumbnail-placeholder">
                  <div className="video-carousel__thumbnail-play-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <span className="video-carousel__thumbnail-title">{video.title}</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="video-carousel__dots">
        {videoList.map((_, index) => (
          <button
            key={index}
            className={`video-carousel__dot ${index === currentVideoIndex ? 'video-carousel__dot--active' : ''}`}
            onClick={() => handleVideoSelect(index)}
            aria-label={`Go to video ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
